import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { flushSync } from "react-dom";
import {
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  FileText,
  Undo2,
} from "lucide-react";
import { timetableApi } from "@/services/api/timetable";
import type { TimetableEntry } from "@/services/api/timetable";
import { TimetableGridCSS } from "./TimetableGridCSS";
import type { TimetableGridEntry } from "./typesV2";
import { EntryEditPopup } from "./EntryEditPopup";
import { SubjectRebindPanel } from "./SubjectRebindPanel";
import type {
  RebindCategory,
  SubjectRebindPreview,
} from "@/services/api/timetable";
import type { SubjectRebindSaveOpts } from "./SubjectRebindPanel";
import { AdjustValidityButton } from "./AdjustValidityButton";
import { AdjustValidityDialog } from "./AdjustValidityDialog";
import { DeleteValidityDialog } from "./DeleteValidityDialog";
import { useUndo } from "@/hooks/useUndo";
import { useLetterGlas } from "@/hooks/useLetterGlas";
import { useToast } from "@/components/ui/ToastProvider";
import {
  LetterGlasGroup,
  LetterGlasRail,
} from "@/components/ui/LetterGlasRail";
import { TeacherCode } from "@/components/teacher/TeacherCode";
import { isValidUUID } from "@/utils/uuid";
import { extractErrorMessage } from "@/utils/errorHandler";
import { exclusionHatchStyle, exclusionTitle } from "@/utils/exclusionHatch";
import { DeleteProgressDialog } from "@/components/common/DeleteProgressDialog";
import { PdfTimetableModal } from "@/components/dataquality/PdfTimetableModal";
import { GapCellPopover } from "@/components/dataquality/GapCellPopover";
import type { SpanEntry } from "@/utils/spanAction";
import {
  adjacentSlot,
  fusionNeighbor,
  type SchoolSlot,
} from "@/utils/spanNeighbor";
import {
  formatPeriodLabel,
  groupEntriesByPeriod,
  isPeriodCurrent,
} from "@/utils/timetableGrouping";

interface TimetablePeriod {
  validFrom: string | null;
  validTo: string | null;
  entries: TimetableEntry[];
}

interface TimetablesByTeacher {
  teacherId: string;
  teacherName: string;
  teacherLastName: string;
  teacherFirstName: string;
  teacherCode?: string;
  periods: TimetablePeriod[];
}

const DAY_INT_TO_STR: Record<number, string> = {
  0: "MON",
  1: "TUE",
  2: "WED",
  3: "THU",
  4: "FRI",
  5: "SAT",
  6: "SUN",
};

const DAY_STR_TO_INT: Record<string, number> = {
  MON: 0,
  TUE: 1,
  WED: 2,
  THU: 3,
  FRI: 4,
  SAT: 5,
  SUN: 6,
};

const UNASSIGNED_DAY = "__UNASSIGNED_DAY__";
const UNASSIGNED_TIME = "__UNASSIGNED_TIME__";

type EntryEditorState =
  | { kind: "edit"; entry: TimetableEntry }
  | {
      kind: "create";
      teacherId: string;
      period: TimetablePeriod;
      prefill: TimetableGridEntry;
    };

function emptyGridEntry(
  teacherCode?: string,
  day?: string | null,
  timeSlot?: string | null
): TimetableGridEntry {
  const resolvedDay = day && day !== UNASSIGNED_DAY ? day : null;
  const resolvedTime =
    timeSlot && timeSlot !== UNASSIGNED_TIME ? timeSlot : null;
  return {
    teacher_code: teacherCode ?? null,
    day_of_week: resolvedDay,
    time_slot_label: resolvedTime,
    lesson_number: null,
    subject: null,
    room: null,
    class_name: null,
    is_multi_period: false,
    lesson_count: 1,
  };
}

function gridEntryToPayload(entry: TimetableGridEntry) {
  const day =
    entry.day_of_week != null ? DAY_STR_TO_INT[entry.day_of_week] : undefined;
  const date = entry.date?.trim() || null;
  return {
    time_slot_label: entry.time_slot_label?.trim() || undefined,
    day_of_week: day,
    subject_name: entry.subject?.trim() || "",
    room_name: entry.room?.trim() || "",
    klass_name: entry.class_name?.trim() || "",
    lesson_number: entry.lesson_number ?? undefined,
    date,
    is_recurring: !date,
  };
}

type TeacherSortKey = "name" | "code";

function sortTeachers(
  teachers: TimetablesByTeacher[],
  key: TeacherSortKey = "name"
): TimetablesByTeacher[] {
  return [...teachers].sort((a, b) => {
    if (key === "code") {
      const ac = a.teacherCode?.trim() || "\uffff";
      const bc = b.teacherCode?.trim() || "\uffff";
      return (
        ac.localeCompare(bc, "de-CH") ||
        (a.teacherLastName || a.teacherName).localeCompare(
          b.teacherLastName || b.teacherName,
          "de-CH"
        ) ||
        (a.teacherFirstName || "").localeCompare(
          b.teacherFirstName || "",
          "de-CH"
        )
      );
    }
    return (
      (a.teacherLastName || a.teacherName).localeCompare(
        b.teacherLastName || b.teacherName,
        "de-CH"
      ) ||
      (a.teacherFirstName || "").localeCompare(
        b.teacherFirstName || "",
        "de-CH"
      )
    );
  });
}

function groupEntriesByTeacher(
  entries: TimetableEntry[]
): TimetablesByTeacher[] {
  const teacherMap = new Map<
    string,
    {
      teacherName: string;
      teacherLastName: string;
      teacherFirstName: string;
      teacherCode?: string;
      entries: TimetableEntry[];
    }
  >();

  for (const entry of entries) {
    const teacherId = entry.teacher_id;
    if (!teacherMap.has(teacherId)) {
      teacherMap.set(teacherId, {
        teacherName: formatTeacherListName(entry),
        teacherLastName: entry.teacher_last_name?.trim() || "",
        teacherFirstName: entry.teacher_first_name?.trim() || "",
        teacherCode: entry.teacher_code,
        entries: [],
      });
    }
    teacherMap.get(teacherId)!.entries.push(entry);
  }

  const result: TimetablesByTeacher[] = [];
  for (const [teacherId, data] of teacherMap.entries()) {
    result.push({
      teacherId,
      teacherName: data.teacherName,
      teacherLastName: data.teacherLastName,
      teacherFirstName: data.teacherFirstName,
      teacherCode: data.teacherCode,
      periods: groupEntriesByPeriod<TimetableEntry>(data.entries),
    });
  }
  return sortTeachers(result);
}

async function fetchTimetableEntries(
  params: Record<string, string | number | boolean> = {}
): Promise<TimetableEntry[]> {
  const allEntries: TimetableEntry[] = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const response = await timetableApi.list({
      page,
      page_size: 500,
      ...params,
    });
    allEntries.push(...response.items);
    totalPages = response.pagination.total_pages;
    page++;
  }
  return allEntries.filter((entry) => isValidUUID(entry.teacher_id));
}

function formatTeacherListName(entry: TimetableEntry): string {
  const last = entry.teacher_last_name?.trim();
  const first = entry.teacher_first_name?.trim();
  if (last && first) return `${last}, ${first}`;
  if (last) return last;
  if (first) return first;
  return entry.teacher_name || "Unbekannt";
}

function convertToGridEntry(entry: TimetableEntry): TimetableGridEntry {
  const dayStr =
    entry.day_of_week !== undefined && entry.day_of_week !== null
      ? (DAY_INT_TO_STR[entry.day_of_week] ?? null)
      : null;

  let timeSlotLabel = entry.time_slot_name ?? null;
  if (!timeSlotLabel && entry.start_time && entry.end_time) {
    const start = entry.start_time.substring(0, 5);
    const end = entry.end_time.substring(0, 5);
    timeSlotLabel = `${start}-${end}`;
  }

  return {
    id: entry.id,
    teacher_code: entry.teacher_code ?? null,
    day_of_week: dayStr,
    time_slot_label: timeSlotLabel,
    lesson_number: null,
    subject: entry.subject_name ?? null,
    subject_id: entry.subject_id ?? null,
    subject_token: entry.subject_token ?? null,
    subject_unclear: entry.subject_unclear ?? false,
    room: entry.room_name ?? null,
    class_name: entry.klass_name ?? null,
    is_multi_period: false,
    lesson_count: 1,
    multi_period_position: null,
    is_manual_entry: entry.is_manual_entry,
    is_cancelled: entry.cancelled,
    cancelled_reason: entry.cancelled_reason ?? null,
    date: entry.date ?? null,
    substitution_excluded: entry.substitution_excluded ?? false,
  };
}

interface TimetablesTabProps {
  onDeleteSuccess?: (count: number) => void;
  reloadKey?: number;
  showLoadLink?: boolean;
}

const NO_RIGHTS = { load: false, delete: false, edit: false };

export function TimetablesTab({
  onDeleteSuccess,
  reloadKey,
  showLoadLink = false,
}: TimetablesTabProps) {
  const [timetablesByTeacher, setTimetablesByTeacher] = useState<
    TimetablesByTeacher[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(
    new Set()
  );
  const [selectedTeachers, setSelectedTeachers] = useState<Set<string>>(
    new Set()
  );
  const [expandedTeachers, setExpandedTeachers] = useState<Set<string>>(
    new Set()
  );
  const [sortKey, setSortKey] = useState<TeacherSortKey>("name");
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({
    current: 0,
    total: 0,
  });
  const [success, setSuccess] = useState<string | null>(null);

  // Adjust Validity Dialog state
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);

  // Delete Validity Dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [entryEditor, setEntryEditor] = useState<EntryEditorState | null>(null);
  const [spanEdit, setSpanEdit] = useState<{
    entry: TimetableEntry;
    periodEntries: TimetableEntry[];
    anchor: DOMRect;
  } | null>(null);
  const [slots, setSlots] = useState<SchoolSlot[]>([]);
  const [spanBusy, setSpanBusy] = useState(false);
  const [spanError, setSpanError] = useState<string | null>(null);
  const [undoStack, setUndoStack] = useState<
    Array<{ undoId: string; teacherId: string }>
  >([]);
  const [rebind, setRebind] = useState<{
    preview: SubjectRebindPreview;
    anchor: DOMRect;
    teacherId: string;
    source?: RebindCategory;
  } | null>(null);
  const [rebindSaving, setRebindSaving] = useState(false);
  const [rebindError, setRebindError] = useState<string | null>(null);
  const [pdfModalTeacher, setPdfModalTeacher] = useState<string | null>(null);
  const [rights, setRights] = useState(NO_RIGHTS);
  const canEdit = rights.edit;
  const canDelete = rights.delete;
  const canSelect = canEdit || canDelete;
  const clickTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (clickTimerRef.current) window.clearTimeout(clickTimerRef.current);
    },
    []
  );

  // Toast and Undo
  const { showToast, updateToast, hideToast } = useToast();
  const [, setLastChangeId] = useState<string | null>(null);
  const [undoToastId, setUndoToastId] = useState<string | null>(null);

  const { canUndo, remainingSeconds, startUndo, triggerUndo } = useUndo({
    duration: 300000, // 5 minutes
    onUndoSuccess: () => {
      loadTimetables();
      showToast({
        message: "Änderung erfolgreich rückgängig gemacht",
        type: "success",
        duration: 3000,
      });
      if (undoToastId) {
        hideToast(undoToastId);
        setUndoToastId(null);
      }
    },
    onUndoError: (error) => {
      showToast({
        message: error,
        type: "error",
        duration: 5000,
      });
    },
  });

  const loadTimetables = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const validEntries = await fetchTimetableEntries();
      let schoolSlots: SchoolSlot[] = [];
      try {
        schoolSlots = await timetableApi.listSlots();
      } catch {
        schoolSlots = [];
      }
      setSlots(schoolSlots);
      setTimetablesByTeacher(groupEntriesByTeacher(validEntries));
    } catch {
      setError("Stundenpläne konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadTeacher = useCallback(async (teacherId: string) => {
    const entries = await fetchTimetableEntries({ teacher_id: teacherId });
    const grouped = groupEntriesByTeacher(entries);
    setTimetablesByTeacher((prev) => {
      const without = prev.filter((t) => t.teacherId !== teacherId);
      if (grouped.length === 0) return without;
      return sortTeachers([...without, ...grouped]);
    });
  }, []);

  useEffect(() => {
    loadTimetables();
  }, [loadTimetables, reloadKey]);

  useEffect(() => {
    let live = true;
    timetableApi
      .adminRights()
      .then((next) => {
        if (live) setRights(next);
      })
      .catch(() => {
        if (live) setRights(NO_RIGHTS);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  const totalEntries = useMemo(
    () =>
      timetablesByTeacher.reduce(
        (sum, t) =>
          sum + t.periods.reduce((pSum, p) => pSum + p.entries.length, 0),
        0
      ),
    [timetablesByTeacher]
  );

  const sortedTeachers = useMemo(
    () => sortTeachers(timetablesByTeacher, sortKey),
    [timetablesByTeacher, sortKey]
  );
  const getIndexText = useCallback(
    (t: TimetablesByTeacher) =>
      sortKey === "code"
        ? (t.teacherCode ?? "")
        : t.teacherLastName || t.teacherName,
    [sortKey]
  );
  const {
    letter,
    setLetter,
    hoverLetter,
    setHoverLetter,
    focusLetter,
    have: letterHave,
    letters: railLetters,
    grouped: teachersGrouped,
  } = useLetterGlas(sortedTeachers, getIndexText);

  const visibleTeachers = useMemo(() => {
    if (!letter) return sortedTeachers;
    return teachersGrouped.find(([L]) => L === letter)?.[1] ?? [];
  }, [letter, sortedTeachers, teachersGrouped]);

  const visibleEntries = useMemo(
    () => visibleTeachers.flatMap((t) => t.periods.flatMap((p) => p.entries)),
    [visibleTeachers]
  );

  const allSelected =
    visibleEntries.length > 0 &&
    visibleEntries.every((e) => selectedEntries.has(e.id));

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      const next = new Set(selectedEntries);
      visibleEntries.forEach((e) => next.delete(e.id));
      const nextTeachers = new Set(selectedTeachers);
      visibleTeachers.forEach((t) => nextTeachers.delete(t.teacherId));
      setSelectedEntries(next);
      setSelectedTeachers(nextTeachers);
    } else {
      const next = new Set(selectedEntries);
      visibleEntries.forEach((e) => next.add(e.id));
      const nextTeachers = new Set(selectedTeachers);
      visibleTeachers.forEach((t) => nextTeachers.add(t.teacherId));
      setSelectedEntries(next);
      setSelectedTeachers(nextTeachers);
    }
  }, [
    allSelected,
    selectedEntries,
    selectedTeachers,
    visibleEntries,
    visibleTeachers,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === "a" &&
        !showAdjustDialog &&
        !showDeleteDialog &&
        !entryEditor
      ) {
        e.preventDefault();
        handleSelectAll();
      }
      if (e.key === "Escape") {
        if (showAdjustDialog) setShowAdjustDialog(false);
        if (showDeleteDialog) setShowDeleteDialog(false);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "u" && canUndo) {
        e.preventDefault();
        triggerUndo();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    showAdjustDialog,
    showDeleteDialog,
    entryEditor,
    canUndo,
    triggerUndo,
    handleSelectAll,
  ]);

  const handleToggleTeacher = (teacherId: string) => {
    const newExpanded = new Set(expandedTeachers);
    if (newExpanded.has(teacherId)) {
      newExpanded.delete(teacherId);
    } else {
      newExpanded.add(teacherId);
    }
    setExpandedTeachers(newExpanded);
  };

  const handleExpandAll = () => {
    setExpandedTeachers((prev) => {
      const next = new Set(prev);
      visibleTeachers.forEach((t) => next.add(t.teacherId));
      return next;
    });
  };

  const handleCollapseAll = () => {
    setExpandedTeachers(new Set());
  };

  const allExpanded =
    visibleTeachers.length > 0 &&
    visibleTeachers.every((t) => expandedTeachers.has(t.teacherId));

  const handleSelectTeacher = (teacherId: string) => {
    const teacher = timetablesByTeacher.find((t) => t.teacherId === teacherId);
    if (!teacher) return;

    const allTeacherEntries = teacher.periods.flatMap((p) => p.entries);
    const allTeacherSelected = allTeacherEntries.every((e) =>
      selectedEntries.has(e.id)
    );

    const newSelected = new Set(selectedEntries);
    const newTeachers = new Set(selectedTeachers);

    if (allTeacherSelected) {
      allTeacherEntries.forEach((e) => newSelected.delete(e.id));
      newTeachers.delete(teacherId);
    } else {
      allTeacherEntries.forEach((e) => newSelected.add(e.id));
      newTeachers.add(teacherId);
    }

    setSelectedEntries(newSelected);
    setSelectedTeachers(newTeachers);
  };

  const handleSelectEntry = (entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);

    const teacher = timetablesByTeacher.find((t) =>
      t.periods.some((p) => p.entries.some((e) => e.id === entryId))
    );
    if (teacher) {
      const allTeacherEntries = teacher.periods.flatMap((p) => p.entries);
      const allSelected = allTeacherEntries.every((e) => newSelected.has(e.id));
      const newTeachers = new Set(selectedTeachers);
      if (allSelected) {
        newTeachers.add(teacher.teacherId);
      } else {
        newTeachers.delete(teacher.teacherId);
      }
      setSelectedTeachers(newTeachers);
    }
  };

  const selectedEntriesList = useMemo<TimetableEntry[]>(() => {
    const entries: TimetableEntry[] = [];
    for (const t of visibleTeachers) {
      for (const p of t.periods) {
        for (const e of p.entries) {
          if (selectedEntries.has(e.id)) {
            entries.push(e);
          }
        }
      }
    }
    return entries;
  }, [visibleTeachers, selectedEntries]);

  const visibleSelectedTeacherIds = useMemo(
    () =>
      visibleTeachers
        .filter((t) => selectedTeachers.has(t.teacherId))
        .map((t) => t.teacherId),
    [visibleTeachers, selectedTeachers]
  );

  const handleDelete = async (entryIdsToDelete?: Set<string>) => {
    const visibleIds = new Set(visibleEntries.map((e) => e.id));
    const raw = entryIdsToDelete ?? selectedEntries;
    const ids = entryIdsToDelete
      ? raw
      : new Set([...raw].filter((id) => visibleIds.has(id)));
    if (ids.size === 0) return;
    setShowDeleteDialog(false);
    const totalToDelete = ids.size;
    setDeleting(true);
    setDeleteProgress({ current: 0, total: totalToDelete });
    setError(null);
    setSuccess(null);

    let deletedCount = 0;
    const failedIds: string[] = [];
    const errorMessages: string[] = [];

    for (const entryId of ids) {
      try {
        await timetableApi.delete(entryId);
        deletedCount++;
        flushSync(() => {
          setDeleteProgress({ current: deletedCount, total: totalToDelete });
        });
      } catch (err) {
        failedIds.push(entryId);
        const msg = extractErrorMessage(err, {
          loadError: `Eintrag ${entryId} konnte nicht gelöscht werden`,
        });
        errorMessages.push(msg);
      }
    }

    setDeleting(false);
    setDeleteProgress({ current: 0, total: 0 });
    setSelectedEntries((prev) => {
      const newSet = new Set(prev);
      failedIds.forEach((id) => newSet.delete(id));
      return newSet;
    });

    if (deletedCount > 0) {
      setSuccess(`${deletedCount} Einträge gelöscht.`);
      onDeleteSuccess?.(deletedCount);
      setSelectedEntries(new Set());
      setSelectedTeachers(new Set());
      await loadTimetables();
    }

    if (failedIds.length > 0) {
      const errorMsg = `${failedIds.length} Einträge konnten nicht gelöscht werden.${errorMessages.length > 0 ? " " + errorMessages[0] : ""}`;
      setError(errorMsg);
      console.error("Delete errors:", errorMessages);
    }
  };

  const openSubjectRebind = async (
    entry: TimetableEntry,
    anchor?: DOMRect,
    source?: RebindCategory
  ) => {
    const box = anchor ?? new DOMRect(24, 120, 80, 40);
    try {
      const preview = await timetableApi.previewSubjectRebind(entry.id);
      setRebindError(null);
      setRebind({ preview, anchor: box, teacherId: entry.teacher_id, source });
    } catch (err) {
      showToast({
        message: extractErrorMessage(err, {
          loadError: "Fachliste konnte nicht geladen werden",
          saveError: "Fachliste konnte nicht geladen werden",
        }),
        type: "error",
        duration: 5000,
      });
    }
  };

  const markEntriesRebound = (
    teacherId: string,
    updatedIds: string[],
    patch: Partial<TimetableEntry>
  ) => {
    const ids = new Set(updatedIds);
    setTimetablesByTeacher((prev) =>
      prev.map((t) => {
        if (t.teacherId !== teacherId) return t;
        return {
          ...t,
          periods: t.periods.map((p) => ({
            ...p,
            entries: p.entries.map((e) =>
              ids.has(e.id)
                ? {
                    ...e,
                    ...patch,
                  }
                : e
            ),
          })),
        };
      })
    );
  };

  const persistSubjectRebind = async (opts: SubjectRebindSaveOpts) => {
    if (!rebind) return;
    setRebindSaving(true);
    setRebindError(null);
    try {
      const result = await timetableApi.rebindSubject(rebind.preview.entry_id, {
        subject_id: opts.createTarget ? undefined : opts.targetId,
        target_id: opts.createTarget ? undefined : opts.targetId,
        source_category: opts.sourceCategory,
        target_category: opts.targetCategory,
        include_siblings: opts.includeSiblings,
        create_alias: opts.createAlias,
        create_target: opts.createTarget,
        create_name: opts.createName,
      });
      const n = result.updated_ids.length;
      const alias = result.alias_created ? " · Alias gespeichert" : "";
      const teacherId = rebind.teacherId;
      const undo = result.undo;
      const recategorize = opts.sourceCategory !== opts.targetCategory;
      const patch: Partial<TimetableEntry> = {};
      if (opts.targetCategory === "subject") {
        patch.subject_id = result.subject_id ?? result.target_id;
        patch.subject_unclear = false;
        if (result.token) patch.subject_name = result.token;
        else
          patch.subject_name =
            result.target_name ?? result.subject_name ?? undefined;
      } else if (opts.targetCategory === "room") {
        patch.room_id = result.target_id;
        patch.room_name = result.target_name;
      } else {
        patch.klass_id = result.target_id;
        patch.klass_name = result.target_name;
      }
      if (recategorize && opts.sourceCategory === "subject") {
        patch.subject_id = undefined;
        patch.subject_name = undefined;
        patch.subject_unclear = false;
      }
      if (recategorize && opts.sourceCategory === "room") {
        patch.room_id = undefined;
        patch.room_name = undefined;
      }
      if (recategorize && opts.sourceCategory === "klass") {
        patch.klass_id = undefined;
        patch.klass_name = undefined;
      }
      markEntriesRebound(teacherId, result.updated_ids, patch);
      showToast({
        message: `${n} Lektion${n === 1 ? "" : "en"} → ${result.target_name || result.subject_name}${alias}`,
        type: "success",
        duration: 8000,
        action: undo
          ? {
              label: "Rückgängig",
              onClick: () => {
                void timetableApi
                  .undoSubjectRebind(undo)
                  .then(() => reloadTeacher(teacherId))
                  .catch((err) => {
                    showToast({
                      message: extractErrorMessage(err, {
                        loadError: "Rückgängig fehlgeschlagen",
                        saveError: "Rückgängig fehlgeschlagen",
                      }),
                      type: "error",
                      duration: 5000,
                    });
                  });
              },
            }
          : undefined,
      });
      setRebind(null);
      try {
        await reloadTeacher(teacherId);
      } catch {
        /* local patch already applied */
      }
    } catch (err) {
      setRebindError(
        extractErrorMessage(err, {
          loadError: "Fach konnte nicht umgehängt werden",
          saveError: "Fach konnte nicht umgehängt werden",
        })
      );
    } finally {
      setRebindSaving(false);
    }
  };

  const spanNeighbor = (
    entry: TimetableEntry,
    entries: TimetableEntry[],
    direction: "up" | "down"
  ) => {
    return fusionNeighbor(entries, entry, direction);
  };

  const toSpanEntry = (entry: TimetableEntry): SpanEntry => ({
    id: entry.id,
    day_of_week: entry.day_of_week ?? null,
    lesson_number: entry.lesson_number ?? null,
    time_slot_name: entry.time_slot_name ?? null,
  });

  const extendSpan = async (direction: "up" | "down") => {
    if (!spanEdit || spanBusy) return;
    const neighbor = spanNeighbor(
      spanEdit.entry,
      spanEdit.periodEntries,
      direction
    );
    setSpanBusy(true);
    setSpanError(null);
    try {
      const result = await timetableApi.extendSpan(
        spanEdit.entry.id,
        direction
      );
      const teacherId = spanEdit.entry.teacher_id;
      if (result.undo_id) {
        setUndoStack((prev) => [
          ...prev,
          { undoId: result.undo_id!, teacherId },
        ]);
      }
      showToast({
        message: neighbor
          ? "Lektionen fusioniert"
          : direction === "up"
            ? "Nach oben erweitert"
            : "Nach unten erweitert",
        type: "success",
        duration: 3000,
      });
      setSpanEdit(null);
      await reloadTeacher(teacherId);
    } catch (err) {
      setSpanError(
        extractErrorMessage(err, {
          loadError: "Erweitern fehlgeschlagen",
          saveError: "Erweitern fehlgeschlagen",
        })
      );
    } finally {
      setSpanBusy(false);
    }
  };

  const undoLastSpan = async () => {
    const last = undoStack[undoStack.length - 1];
    if (!last || spanBusy) return;
    setSpanBusy(true);
    setSpanError(null);
    try {
      await timetableApi.undoExtend(last.undoId);
      setUndoStack((prev) => prev.slice(0, -1));
      showToast({ message: "Zurückgenommen", type: "success", duration: 3000 });
      await reloadTeacher(last.teacherId);
    } catch (err) {
      const message = extractErrorMessage(err, {
        loadError: "Rückgängig fehlgeschlagen",
        saveError: "Rückgängig fehlgeschlagen",
      });
      setSpanError(message);
      showToast({ message, type: "error", duration: 5000 });
    } finally {
      setSpanBusy(false);
    }
  };

  const persistEditedEntry = async (
    existing: TimetableEntry,
    updated: TimetableGridEntry
  ) => {
    const payload = gridEntryToPayload(updated);
    if (payload.day_of_week === undefined && !payload.date) {
      const message = "Wochentag oder Datum ist erforderlich";
      showToast({ message, type: "error", duration: 5000 });
      throw new Error(message);
    }
    if (!payload.time_slot_label) {
      const message = "Zeit ist erforderlich";
      showToast({ message, type: "error", duration: 5000 });
      throw new Error(message);
    }
    try {
      await timetableApi.update(existing.id, payload);
      showToast({
        message: "Lektion gespeichert",
        type: "success",
        duration: 3000,
      });
      await reloadTeacher(existing.teacher_id);
    } catch (err) {
      const message = extractErrorMessage(err, {
        loadError: "Lektion konnte nicht gespeichert werden",
        saveError: "Lektion konnte nicht gespeichert werden",
      });
      showToast({ message, type: "error", duration: 5000 });
      throw err;
    }
  };

  const persistNewEntry = async (
    teacherId: string,
    period: TimetablePeriod,
    updated: TimetableGridEntry
  ) => {
    const payload = gridEntryToPayload(updated);
    if (payload.day_of_week === undefined && !payload.date) {
      const message = "Wochentag oder Datum ist erforderlich";
      showToast({ message, type: "error", duration: 5000 });
      throw new Error(message);
    }
    if (!payload.time_slot_label) {
      const message = "Zeit ist erforderlich";
      showToast({ message, type: "error", duration: 5000 });
      throw new Error(message);
    }
    try {
      await timetableApi.create({
        teacher_id: teacherId,
        time_slot_label: payload.time_slot_label,
        day_of_week: payload.day_of_week,
        subject_name: payload.subject_name,
        room_name: payload.room_name,
        klass_name: payload.klass_name,
        lesson_number: payload.lesson_number,
        date: payload.date ?? undefined,
        is_recurring: payload.is_recurring,
        valid_from: period.validFrom,
        valid_to: period.validTo,
      });
      showToast({
        message: "Lektion hinzugefügt",
        type: "success",
        duration: 3000,
      });
      await reloadTeacher(teacherId);
    } catch (err) {
      const message = extractErrorMessage(err, {
        loadError: "Lektion konnte nicht erstellt werden",
        saveError: "Lektion konnte nicht erstellt werden",
      });
      showToast({ message, type: "error", duration: 5000 });
      throw err;
    }
  };

  const persistDeleteEntry = async (entryId: string, teacherId: string) => {
    try {
      await timetableApi.delete(entryId);
      showToast({
        message: "Lektion gelöscht",
        type: "success",
        duration: 3000,
      });
      setSelectedEntries((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
      await reloadTeacher(teacherId);
    } catch (err) {
      const message = extractErrorMessage(err, {
        loadError: "Lektion konnte nicht gelöscht werden",
      });
      showToast({ message, type: "error", duration: 5000 });
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
        <span className="text-gray-500">Stundenpläne laden...</span>
      </div>
    );
  }

  if (error && timetablesByTeacher.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <AlertCircle className="w-6 h-6 text-red-500 mr-2" />
        <span className="text-red-600">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {deleting && (
        <DeleteProgressDialog
          title="Lösche Einträge..."
          current={deleteProgress.current}
          total={deleteProgress.total}
          status={`${deleteProgress.current} von ${deleteProgress.total} gelöscht`}
          hint="Bitte warten, der Prozess läuft..."
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">
            {timetablesByTeacher.length} Lehrer, {totalEntries} Einträge
          </p>
        </div>
        <div className="flex items-center gap-3">
          {showLoadLink && rights.load && (
            <Link
              to="/admin/timetable-laden"
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
            >
              Aktualisierten Plan laden
            </Link>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => void undoLastSpan()}
              disabled={undoStack.length === 0 || spanBusy}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Undo2 className="w-4 h-4" />
              Rückgängig
            </button>
          )}
          {canDelete && selectedEntriesList.length > 0 && (
            <span className="text-sm text-gray-600">
              {selectedEntriesList.length} ausgewählt
            </span>
          )}
          {canDelete && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              disabled={selectedEntriesList.length === 0 || deleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? "Löschen..." : "Löschen"}
            </button>
          )}
          {canEdit && (
            <AdjustValidityButton
              selectedCount={visibleSelectedTeacherIds.length}
              onClick={() => setShowAdjustDialog(true)}
              disabled={deleting}
            />
          )}
        </div>
      </div>

      {success && (
        <div className="p-3 bg-green-100 border border-green-200 rounded flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <span className="text-green-700 text-sm">{success}</span>
        </div>
      )}

      {error && timetablesByTeacher.length > 0 && (
        <div className="p-3 bg-red-100 border border-red-200 rounded flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b flex items-center gap-3">
          {canSelect && (
            <input
              type="checkbox"
              checked={allSelected}
              onChange={handleSelectAll}
              aria-label="Alle auswählen"
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          )}
          {canSelect && (
            <span className="text-sm font-medium text-gray-700">
              Alle auswählen
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <div className="inline-flex overflow-hidden rounded border border-gray-200 text-xs font-semibold">
              <button
                type="button"
                className={`px-2 py-1 ${sortKey === "name" ? "bg-blue-600 text-white" : "bg-white text-gray-500"}`}
                onClick={() => {
                  setSortKey("name");
                  setLetter(null);
                  setHoverLetter(null);
                }}
              >
                A–Z Name
              </button>
              <button
                type="button"
                className={`px-2 py-1 ${sortKey === "code" ? "bg-blue-600 text-white" : "bg-white text-gray-500"}`}
                onClick={() => {
                  setSortKey("code");
                  setLetter(null);
                  setHoverLetter(null);
                }}
              >
                A–Z Kürzel
              </button>
            </div>
            <button
              onClick={allExpanded ? handleCollapseAll : handleExpandAll}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {allExpanded ? "Alle schliessen" : "Alle öffnen"}
            </button>
          </div>
        </div>

        <div className="flex max-h-[600px] items-stretch">
          {timetablesByTeacher.length > 0 && (
            <LetterGlasRail
              letters={railLetters}
              have={letterHave}
              letter={letter}
              hoverLetter={hoverLetter}
              focusLetter={focusLetter}
              ariaLabelPrefix={sortKey === "code" ? "Kürzel" : "Nachname"}
              onHover={setHoverLetter}
              onPick={setLetter}
            />
          )}
          <div className="min-w-0 flex-1 divide-y overflow-y-auto">
            {teachersGrouped.map(([L, teachers]) => {
              const out = !!focusLetter && focusLetter !== L;
              return (
                <LetterGlasGroup key={L} letter={L} hidden={out}>
                  {teachers.map((teacher) => {
                    const allTeacherEntries = teacher.periods.flatMap(
                      (p) => p.entries
                    );
                    const allTeacherSelected = allTeacherEntries.every((e) =>
                      selectedEntries.has(e.id)
                    );
                    const someTeacherSelected =
                      allTeacherEntries.some((e) =>
                        selectedEntries.has(e.id)
                      ) && !allTeacherSelected;
                    const isExpanded = expandedTeachers.has(teacher.teacherId);

                    return (
                      <div key={teacher.teacherId} className="bg-white">
                        <div
                          className="px-4 py-2 bg-gray-50 border-b flex items-center gap-3 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleToggleTeacher(teacher.teacherId)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                          {canSelect && (
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={allTeacherSelected}
                                ref={(el) => {
                                  if (el) {
                                    el.indeterminate = someTeacherSelected;
                                  }
                                }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectTeacher(teacher.teacherId);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                tabIndex={out ? -1 : undefined}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </div>
                          )}
                          <TeacherCode code={teacher.teacherCode} />
                          <span className="min-w-0 truncate text-[13px] font-semibold">
                            {teacher.teacherName}
                          </span>
                          <button
                            type="button"
                            aria-label="PDF-Stundenplan"
                            title="PDF-Stundenplan"
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-800 hover:bg-red-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPdfModalTeacher(teacher.teacherId);
                            }}
                          >
                            <FileText className="w-3 h-3" />
                            PDF
                          </button>
                          <span className="text-sm text-gray-500">
                            {allTeacherEntries.length} Einträge
                          </span>
                          {(() => {
                            const unclear = allTeacherEntries.filter(
                              (e) => e.subject_unclear
                            ).length;
                            return unclear > 0 ? (
                              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                                {unclear} unklar
                              </span>
                            ) : null;
                          })()}
                          {teacher.periods.length > 1 && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                              {teacher.periods.length} Perioden
                            </span>
                          )}
                        </div>

                        {isExpanded && (
                          <div>
                            {teacher.periods.map((period, periodIdx) => {
                              const periodLabel = formatPeriodLabel(
                                period.validFrom,
                                period.validTo
                              );
                              const isCurrent = isPeriodCurrent(
                                period.validFrom,
                                period.validTo
                              );

                              const handlePeriodClick = () => {
                                if (!isValidUUID(teacher.teacherId)) {
                                  showToast({
                                    message:
                                      "Die Gültigkeit kann für diesen Lehrer nicht angepasst werden (ungültige Lehrer-ID).",
                                    type: "error",
                                    duration: 5000,
                                  });
                                  return;
                                }
                                setSelectedTeachers(
                                  new Set([teacher.teacherId])
                                );
                                setSelectedEntries(
                                  new Set(period.entries.map((e) => e.id))
                                );
                                setShowAdjustDialog(true);
                              };

                              return (
                                <div
                                  key={periodIdx}
                                  className="border-b last:border-b-0"
                                >
                                  <div
                                    onClick={handlePeriodClick}
                                    className={`px-4 py-2 flex items-center gap-2 cursor-pointer hover:bg-blue-50 transition-colors ${isCurrent ? "bg-green-50" : "bg-gray-50"}`}
                                    title="Klicken um Gültigkeit anzupassen"
                                  >
                                    <span
                                      className={`text-sm font-medium ${isCurrent ? "text-green-700" : "text-gray-600"}`}
                                    >
                                      {periodLabel}
                                    </span>
                                    {isCurrent && (
                                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                        Aktuell
                                      </span>
                                    )}
                                    {!isCurrent &&
                                      period.validTo &&
                                      new Date(period.validTo) < new Date() && (
                                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                          Abgelaufen
                                        </span>
                                      )}
                                    <span className="text-xs text-gray-500">
                                      ({period.entries.length} Einträge)
                                    </span>
                                  </div>
                                  <div className="p-4 border-b">
                                    <TimetableGridCSS
                                      entries={period.entries.map(
                                        convertToGridEntry
                                      )}
                                      onCellClick={(
                                        _day,
                                        _timeSlot,
                                        entryIndices,
                                        event,
                                        field
                                      ) => {
                                        if (!canEdit) return;
                                        const entry =
                                          period.entries[entryIndices[0] ?? -1];
                                        if (!entry) return;
                                        const anchor =
                                          event?.currentTarget.getBoundingClientRect();
                                        if (clickTimerRef.current)
                                          window.clearTimeout(
                                            clickTimerRef.current
                                          );
                                        clickTimerRef.current =
                                          window.setTimeout(() => {
                                            if (field) {
                                              void openSubjectRebind(
                                                entry,
                                                anchor,
                                                field
                                              );
                                              return;
                                            }
                                            setSpanError(null);
                                            setSpanEdit({
                                              entry,
                                              periodEntries:
                                                teacher.periods.flatMap(
                                                  (item) => item.entries
                                                ),
                                              anchor:
                                                anchor ??
                                                new DOMRect(24, 120, 80, 40),
                                            });
                                          }, 220);
                                      }}
                                      onCellDoubleClick={(
                                        _day,
                                        _timeSlot,
                                        entryIndices
                                      ) => {
                                        if (!canEdit) return;
                                        if (clickTimerRef.current)
                                          window.clearTimeout(
                                            clickTimerRef.current
                                          );
                                        setSpanEdit(null);
                                        const entry =
                                          period.entries[entryIndices[0] ?? -1];
                                        if (entry)
                                          setEntryEditor({
                                            kind: "edit",
                                            entry,
                                          });
                                      }}
                                      onEmptyCellClick={(day, timeSlot) => {
                                        if (!canEdit) return;
                                        setEntryEditor({
                                          kind: "create",
                                          teacherId: teacher.teacherId,
                                          period,
                                          prefill: emptyGridEntry(
                                            teacher.teacherCode,
                                            day,
                                            timeSlot
                                          ),
                                        });
                                      }}
                                      highlightedCell={null}
                                      highlightedEntryIndices={new Set()}
                                      fitToView={false}
                                      fixedDays={[
                                        "MON",
                                        "TUE",
                                        "WED",
                                        "THU",
                                        "FRI",
                                        "SAT",
                                      ]}
                                    />
                                  </div>
                                  <div className="divide-y">
                                    <div className="px-4 py-2 bg-gray-50 grid grid-cols-[auto_1fr] gap-3 items-center">
                                      <div className="w-4" />
                                      <div className="grid grid-cols-6 gap-4 text-xs font-medium text-gray-500">
                                        <span>Datum</span>
                                        <span>Zeit</span>
                                        <span>Fach</span>
                                        <span>Raum</span>
                                        <span>Klasse</span>
                                        <span>Typ</span>
                                      </div>
                                    </div>
                                    {period.entries.map((entry) => (
                                      <div
                                        key={entry.id}
                                        className={`px-4 py-2 flex items-center gap-3 hover:bg-gray-50 cursor-pointer ${
                                          selectedEntries.has(entry.id)
                                            ? "bg-blue-50"
                                            : ""
                                        }`}
                                        onClick={(e) => {
                                          if (!canEdit) return;
                                          void openSubjectRebind(
                                            entry,
                                            (
                                              e.currentTarget as HTMLElement
                                            ).getBoundingClientRect()
                                          );
                                        }}
                                      >
                                        {canSelect && (
                                          <input
                                            type="checkbox"
                                            checked={selectedEntries.has(
                                              entry.id
                                            )}
                                            onChange={() =>
                                              handleSelectEntry(entry.id)
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            tabIndex={out ? -1 : undefined}
                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                          />
                                        )}
                                        <div className="flex-1 grid grid-cols-6 gap-4 text-sm">
                                          <span className="text-gray-600">
                                            {entry.date
                                              ? new Date(
                                                  entry.date
                                                ).toLocaleDateString("de-CH")
                                              : "—"}
                                          </span>
                                          <span className="text-gray-900">
                                            {entry.time_slot_name || "—"}
                                          </span>
                                          <span
                                            className="text-gray-900"
                                            style={
                                              entry.substitution_excluded
                                                ? exclusionHatchStyle
                                                : undefined
                                            }
                                            title={
                                              entry.substitution_excluded
                                                ? exclusionTitle
                                                : undefined
                                            }
                                          >
                                            {entry.subject_name || "—"}
                                          </span>
                                          <span className="text-gray-600">
                                            {entry.room_name || "—"}
                                          </span>
                                          <span className="text-gray-600">
                                            {entry.klass_name || "—"}
                                          </span>
                                          <span className="text-gray-500 flex items-center justify-between gap-2">
                                            {entry.is_recurring ? (
                                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                                Wiederholend
                                              </span>
                                            ) : entry.is_manual_entry ? (
                                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                                Manuell
                                              </span>
                                            ) : (
                                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                                Importiert
                                              </span>
                                            )}
                                            {canEdit && (
                                              <Pencil className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                    {canEdit && (
                                      <div className="px-4 py-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setEntryEditor({
                                              kind: "create",
                                              teacherId: teacher.teacherId,
                                              period,
                                              prefill: emptyGridEntry(
                                                teacher.teacherCode
                                              ),
                                            })
                                          }
                                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                                        >
                                          <Plus className="w-4 h-4" />
                                          Lektion hinzufügen
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </LetterGlasGroup>
              );
            })}
          </div>
        </div>
      </div>

      {timetablesByTeacher.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Keine Stundenpläne vorhanden.
        </div>
      )}

      {/* Delete Validity Dialog */}
      <DeleteValidityDialog
        isOpen={showDeleteDialog}
        selectedEntries={selectedEntriesList}
        onClose={() => setShowDeleteDialog(false)}
        onConfirmDelete={handleDelete}
        deleting={deleting}
      />

      {/* Adjust Validity Dialog */}
      <AdjustValidityDialog
        isOpen={showAdjustDialog}
        teacherIds={visibleSelectedTeacherIds}
        allowReplace={canDelete}
        onClose={() => setShowAdjustDialog(false)}
        onSuccess={(result) => {
          setShowAdjustDialog(false);
          setSelectedTeachers(new Set());
          setSelectedEntries(new Set());
          loadTimetables();

          // Handle partial success
          if (result.errors && result.errors.length > 0) {
            showToast({
              message: `${result.updated_count} Einträge aktualisiert, ${result.errors.length} Fehler aufgetreten`,
              type: "error",
              duration: 10000,
            });
          }

          // Handle undo with change_id from response
          if (result.change_id && result.updated_count > 0) {
            setLastChangeId(result.change_id);
            startUndo(result.change_id);

            // Show toast with live countdown
            const toastId = showToast({
              message: `Gültigkeit erfolgreich für ${result.affected_teachers || selectedTeachers.size} Lehrer aktualisiert`,
              type: "success",
              duration: 0, // Keep until dismissed
            });
            setUndoToastId(toastId);

            // Update toast every second with remaining time
            const updateInterval = setInterval(() => {
              if (!canUndo) {
                clearInterval(updateInterval);
                hideToast(toastId);
                return;
              }

              const mins = Math.floor(remainingSeconds / 60);
              const secs = remainingSeconds % 60;

              updateToast(toastId, {
                action: {
                  label: `Rückgängig (${mins}:${secs.toString().padStart(2, "0")})`,
                  onClick: () => {
                    triggerUndo();
                  },
                },
              });
            }, 1000);
          } else {
            // No undo available (no entries updated)
            showToast({
              message: "Keine Einträge aktualisiert",
              type: "info",
              duration: 3000,
            });
          }
        }}
      />

      {pdfModalTeacher && (
        <PdfTimetableModal
          teacherId={pdfModalTeacher}
          onClose={() => setPdfModalTeacher(null)}
        />
      )}

      {rebind && (
        <SubjectRebindPanel
          preview={rebind.preview}
          anchor={rebind.anchor}
          initialSource={rebind.source}
          saving={rebindSaving}
          error={rebindError}
          onSave={(opts) => {
            void persistSubjectRebind(opts);
          }}
          onClose={() => {
            if (!rebindSaving) setRebind(null);
          }}
        />
      )}

      {spanEdit && (
        <GapCellPopover
          entry={toSpanEntry(spanEdit.entry)}
          neighborUp={(() => {
            const row = spanNeighbor(
              spanEdit.entry,
              spanEdit.periodEntries,
              "up"
            );
            return row ? toSpanEntry(row) : null;
          })()}
          neighborDown={(() => {
            const row = spanNeighbor(
              spanEdit.entry,
              spanEdit.periodEntries,
              "down"
            );
            return row ? toSpanEntry(row) : null;
          })()}
          canExtendUp={
            spanNeighbor(spanEdit.entry, spanEdit.periodEntries, "up") !=
              null ||
            adjacentSlot(slots, spanEdit.entry.time_slot_id, "up") !== null
          }
          canExtendDown={
            spanNeighbor(spanEdit.entry, spanEdit.periodEntries, "down") !=
              null ||
            adjacentSlot(slots, spanEdit.entry.time_slot_id, "down") !== null
          }
          anchor={spanEdit.anchor}
          busy={spanBusy}
          error={spanError}
          onExtend={(direction) => void extendSpan(direction)}
          onDelete={() => {
            if (!canDelete) return;
            setSpanEdit(null);
            void persistDeleteEntry(
              spanEdit.entry.id,
              spanEdit.entry.teacher_id
            );
          }}
          onEditField={(field) => {
            const entry = spanEdit.entry;
            const anchor = spanEdit.anchor;
            setSpanEdit(null);
            void openSubjectRebind(entry, anchor, field);
          }}
          onClose={() => {
            if (!spanBusy) setSpanEdit(null);
          }}
        />
      )}

      {entryEditor?.kind === "edit" && (
        <EntryEditPopup
          entries={[convertToGridEntry(entryEditor.entry)]}
          entryIndices={[0]}
          mode="dashboard"
          onSave={async (_index, updated) => {
            await persistEditedEntry(entryEditor.entry, updated);
          }}
          onDelete={async () => {
            await persistDeleteEntry(
              entryEditor.entry.id,
              entryEditor.entry.teacher_id
            );
          }}
          onClose={() => setEntryEditor(null)}
        />
      )}

      {entryEditor?.kind === "create" && (
        <EntryEditPopup
          newEntry={entryEditor.prefill}
          mode="dashboard"
          onSaveNew={async (entry) => {
            await persistNewEntry(
              entryEditor.teacherId,
              entryEditor.period,
              entry
            );
          }}
          onClose={() => setEntryEditor(null)}
        />
      )}
    </div>
  );
}
